//----------------------------------------------------------------------------------------------------------------------
// Frame Graph Post-Processing Backend
//
// Builds a directed task chain using BabylonJS Frame Graph system.
// Each post-processing effect is a separate task chained via texture handles.
//----------------------------------------------------------------------------------------------------------------------

import {
    Constants,
    FrameGraph,
    FrameGraphBloomTask,
    FrameGraphChromaticAberrationTask,
    FrameGraphClearTextureTask,
    FrameGraphCopyToBackbufferColorTask,
    FrameGraphGeometryRendererTask,
    FrameGraphGrainTask,
    FrameGraphImageProcessingTask,
    FrameGraphObjectRendererTask,
    FrameGraphSSAO2RenderingPipelineTask,
    FrameGraphSharpenTask,
    type FrameGraphTextureHandle,
    ImageProcessingConfiguration,
    type Scene,
} from '@babylonjs/core';

import type { PostProcessingConfig } from '../interfaces/level.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Map from user-facing tonemap operator names to BabylonJS constants.
 * BabylonJS only has STANDARD (0) and ACES (1) — the other names all map to STANDARD.
 */
const TONEMAP_OPERATORS : Record<string, number> = {
    hable: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
    reinhard: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
    hejidawson: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
    photographic: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
    aces: ImageProcessingConfiguration.TONEMAPPING_ACES,
};

//----------------------------------------------------------------------------------------------------------------------

export async function applyFrameGraphPostProcessing(scene : Scene, config : PostProcessingConfig) : Promise<void>
{
    const camera = scene.activeCamera;
    if(!camera)
    {
        return;
    }

    const engine = scene.getEngine();
    const fg = new FrameGraph(scene);

    // Create the main render target texture
    const colorTexture = fg.textureManager.createRenderTargetTexture('sage-fg-color', {
        size: { width: 100, height: 100 },
        sizeIsPercentage: true,
        options: { formats: [ Constants.TEXTUREFORMAT_RGBA ], samples: 4 },
    });

    // 1. Clear
    const clearTask = new FrameGraphClearTextureTask('sage-clear', fg);
    clearTask.targetTexture = colorTexture;
    fg.addTask(clearTask);

    // 2. Render objects
    const renderTask = new FrameGraphObjectRendererTask('sage-render', fg, scene);
    renderTask.targetTexture = clearTask.outputTexture;
    renderTask.depthTexture = clearTask.outputDepthTexture;
    renderTask.camera = camera;
    renderTask.objectList = { meshes: scene.meshes, particleSystems: scene.particleSystems };
    renderTask.isMainObjectRenderer = true;
    fg.addTask(renderTask);

    // Track the current output texture through the chain
    let currentTexture : FrameGraphTextureHandle = renderTask.outputTexture;

    // 3. SSAO (needs geometry renderer for depth/normals)
    if(config.ssao)
    {
        const geoTask = new FrameGraphGeometryRendererTask('sage-geometry', fg, scene);
        fg.addTask(geoTask);

        const ssaoTask = new FrameGraphSSAO2RenderingPipelineTask('sage-ssao', fg, 0.5, 1.0);
        ssaoTask.sourceTexture = currentTexture;
        ssaoTask.depthTexture = geoTask.geometryViewDepthTexture;
        ssaoTask.normalTexture = geoTask.geometryViewNormalTexture;
        ssaoTask.camera = camera;
        fg.addTask(ssaoTask);

        if(config.ssao.radius !== undefined) { ssaoTask.ssao.radius = config.ssao.radius; }
        if(config.ssao.samples !== undefined) { ssaoTask.ssao.samples = config.ssao.samples; }
        if(config.ssao.totalStrength !== undefined) { ssaoTask.ssao.totalStrength = config.ssao.totalStrength; }

        currentTexture = ssaoTask.outputTexture;
    }

    // 4. Bloom
    if(config.bloom)
    {
        const bloomTask = new FrameGraphBloomTask(
            'sage-bloom',
            fg,
            config.bloom.weight ?? 0.5,
            config.bloom.kernel ?? 128,
            config.bloom.threshold ?? 0.1,
            true,
            config.bloom.scale ?? 0.5
        );
        bloomTask.sourceTexture = currentTexture;
        fg.addTask(bloomTask);

        currentTexture = bloomTask.outputTexture;
    }

    // 5. Grain
    if(config.grain)
    {
        const grainTask = new FrameGraphGrainTask('sage-grain', fg);
        grainTask.sourceTexture = currentTexture;
        fg.addTask(grainTask);

        if(config.grain.intensity !== undefined) { grainTask.postProcess.intensity = config.grain.intensity; }
        if(config.grain.animated !== undefined) { grainTask.postProcess.animated = config.grain.animated; }

        currentTexture = grainTask.outputTexture;
    }

    // 6. Sharpen
    if(config.sharpen)
    {
        const sharpenTask = new FrameGraphSharpenTask('sage-sharpen', fg);
        sharpenTask.sourceTexture = currentTexture;
        fg.addTask(sharpenTask);

        if(config.sharpen.edge !== undefined) { sharpenTask.postProcess.edgeAmount = config.sharpen.edge; }
        if(config.sharpen.color !== undefined) { sharpenTask.postProcess.colorAmount = config.sharpen.color; }

        currentTexture = sharpenTask.outputTexture;
    }

    // 7. Chromatic aberration
    if(config.chromaticAberration)
    {
        const chromTask = new FrameGraphChromaticAberrationTask('sage-chromab', fg);
        chromTask.sourceTexture = currentTexture;
        fg.addTask(chromTask);

        if(config.chromaticAberration.amount !== undefined)
        {
            chromTask.postProcess.aberrationAmount = config.chromaticAberration.amount;
        }

        currentTexture = chromTask.outputTexture;
    }

    // 8. Image processing (tonemap + vignette)
    if(config.tonemap || config.vignette)
    {
        const imgTask = new FrameGraphImageProcessingTask('sage-imgproc', fg);
        imgTask.sourceTexture = currentTexture;
        fg.addTask(imgTask);

        if(config.tonemap)
        {
            imgTask.postProcess.toneMappingEnabled = true;

            if(config.tonemap.operator)
            {
                imgTask.postProcess.toneMappingType
                    = TONEMAP_OPERATORS[config.tonemap.operator]
                    ?? ImageProcessingConfiguration.TONEMAPPING_STANDARD;
            }
        }

        if(config.vignette)
        {
            imgTask.postProcess.vignetteEnabled = true;

            if(config.vignette.weight !== undefined) { imgTask.postProcess.vignetteWeight = config.vignette.weight; }
            if(config.vignette.stretch !== undefined)
            {
                imgTask.postProcess.vignetteStretch = config.vignette.stretch;
            }
            if(config.vignette.color)
            {
                const vc = config.vignette.color;
                imgTask.postProcess.vignetteColor.set(vc.r, vc.g, vc.b, 1);
            }
        }

        currentTexture = imgTask.outputTexture;
    }

    // 9. Volumetric lighting — not yet wired up for automatic light detection
    if(config.volumetric)
    {
        console.warn(
            '[SAGE] Volumetric lighting config is recognized but automatic light detection is not yet supported. '
            + 'Use scene.frameGraph to add FrameGraphVolumetricLightingTask manually.'
        );
    }

    // 10. Output to screen
    const copyTask = new FrameGraphCopyToBackbufferColorTask('sage-output', fg);
    copyTask.sourceTexture = currentTexture;
    fg.addTask(copyTask);

    // Wire up the scene — cameraToUseForPointers is needed because activeCamera becomes null between tasks
    scene.cameraToUseForPointers = camera;
    scene.frameGraph = fg;

    // Rebuild on resize
    engine.onResizeObservable.add(async () =>
    {
        await fg.buildAsync();
    });

    // Initial build
    await fg.buildAsync();
}

//----------------------------------------------------------------------------------------------------------------------
